import { observer } from 'mobx-react-lite';
import {
  Drawer,
  List,
  Typography,
  Button,
  Divider,
  Box,
  Tabs,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
} from '@mui/material';
import { Check, AssignmentOutlined, AssignmentTurnedInOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { AddonStore } from 'stores/AddonStore';
import { useState } from 'react';
import { ReactComponent as CloseDrawerIcon } from 'images/closeIcon.svg';
import AddonCard from './AddonCard';
import { CustomTab } from './CustomTab';
import AmplitudeService from 'services/amplitude.service';

type AddonProps = {
  addonStore: AddonStore;
  fullWidthButton?: boolean;
  jobDetails?: Record<string, any>;
};

const Addon = observer(({ addonStore, fullWidthButton = false, jobDetails = {} }: AddonProps) => {
  const { openDrawer, closeDrawer, submitOrder, isOpen, availableAddons, orderedAddons, totalCost, orderSubmitted } =
    addonStore;

  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  enum TabType {
    AvailableAddons = 0,
    OrderedAddons = 1,
  }

  const [selectedTab, setSelectedTab] = useState(TabType.AvailableAddons);

  const handleTabChange = (newValue: TabType) => {
    setSelectedTab(newValue);
  };

  const renderTab = () => {
    switch (selectedTab) {
      case TabType.AvailableAddons:
        return (
          <>
            <Typography sx={{ padding: '24px' }}>
              These are add-ons currently available for this report. For sent orders press the ordered add-ons button up
              top.
            </Typography>
            <List sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
              {availableAddons.map((addon) => (
                <AddonCard
                  addon={addon}
                  handleAddonClick={(addonId) => addonStore.toggleAddonSelection(addonId, jobDetails)}
                />
              ))}
            </List>
          </>
        );
      case TabType.OrderedAddons:
        return (
          <>
            <Typography sx={{ padding: '24px' }}>
              These are add-ons that have been ordered or purchased for this report.
            </Typography>
            <List sx={{ paddingLeft: '24px', paddingRight: '24px' }}>
              {orderedAddons.map((addon) => (
                <AddonCard addon={addon} handleAddonClick={() => {}} />
              ))}
            </List>
          </>
        );
    }
  };

  const renderFooter = () => {
    if (orderSubmitted) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '16px 24px',
            backgroundColor: theme.palette.grey[50],
            marginBottom: '8px',
          }}
        >
          <Button onClick={closeDrawer} variant="contained" color="primary" sx={{ marginRight: '10px' }}>
            CLOSE
          </Button>
        </Box>
      );
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            padding: '16px 24px',
            marginTop: 'auto',
            backgroundColor: theme.palette.grey[50],
          }}
        >
          {selectedTab === TabType.AvailableAddons && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
              <Typography variant="caption">ESTIMATED ADD-ON TOTAL:</Typography>
              <Typography
                variant="body1"
                sx={{ marginLeft: '8px', fontWeight: 'bold', minWidth: '120px', textAlign: 'right' }}
              >
                ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
            <Button onClick={closeDrawer} variant="outlined" color="primary" sx={{ marginRight: '10px' }}>
              CANCEL
            </Button>
            <Button
              onClick={() => submitOrder(jobDetails)}
              variant="contained"
              color="primary"
              disabled={selectedTab === TabType.OrderedAddons || !availableAddons.some((addon) => addon.isSelected)}
            >
              SEND ADD-ON REQUEST
            </Button>
          </Box>
        </Box>
      );
    }
  };

  const renderBody = () => {
    if (!orderSubmitted) {
      return xsScreen ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '16px 24px 0',
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Select
              value={selectedTab}
              onChange={(event) => handleTabChange(event.target.value as TabType)}
              IconComponent={ExpandMoreIcon}
              sx={{
                borderRadius: '4px',
                background: '#FFF',
                textAlign: 'center',
                textTransform: 'uppercase',
                color: `${theme.palette.primary.main}`,
                fontWeight: 'bold',
                '& .MuiSelect-icon': {
                  color: `${theme.palette.primary.main}`,
                },
              }}
              fullWidth
            >
              <MenuItem value={TabType.AvailableAddons} sx={{ display: 'flex', justifyContent: 'center' }}>
                Available Add-ons
              </MenuItem>
              <MenuItem value={TabType.OrderedAddons} sx={{ display: 'flex', justifyContent: 'center' }}>
                Ordered Add-ons
              </MenuItem>
            </Select>
          </Box>
          {renderTab()}
        </>
      ) : (
        <>
          <Tabs
            value={selectedTab}
            onChange={(event, newValue) => handleTabChange(newValue)}
            data-testid="tabs-container"
            sx={{ paddingLeft: '24px', paddingRight: '24px', marginTop: '16px' }}
            variant="fullWidth"
          >
            <CustomTab
              label={
                <Typography sx={{ display: 'flex', alignItems: 'center', textTransform: 'none' }}>
                  <AssignmentOutlined sx={{ marginRight: '8px' }} />
                  Available Add-ons
                </Typography>
              }
            />
            <CustomTab
              label={
                <Typography sx={{ display: 'flex', alignItems: 'center', textTransform: 'none' }}>
                  <AssignmentTurnedInOutlined sx={{ marginRight: '8px' }} />
                  Ordered Add-ons
                </Typography>
              }
            />
          </Tabs>
          {renderTab()}
        </>
      );
    } else {
      return (
        <>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              marginTop: '24px',
              paddingLeft: '16px',
              paddingRight: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check />
            Add-On Request Sent!
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', paddingTop: '12px', paddingLeft: '16px', paddingRight: '16px' }}
          >
            Your add-on request has been submitted, and our team will respond shortly to process and fulfill your
            purchase. Thank you!
          </Typography>
        </>
      );
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          AmplitudeService.trackEvent('View Report Add-Ons', jobDetails);
          openDrawer();
        }}
        fullWidth={fullWidthButton}
        variant="contained"
      >
        View Report Add-Ons
      </Button>

      <Drawer
        PaperProps={{ sx: { width: xsScreen ? '100%' : '560px' } }}
        anchor="right"
        open={isOpen}
        onClose={closeDrawer}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px' }}>
          <Typography variant="h6">Report Add-ons</Typography>
          <IconButton aria-label="Close drawer" onClick={closeDrawer}>
            <CloseDrawerIcon />
          </IconButton>
        </Box>

        <Divider />

        <Paper sx={{ backgroundColor: theme.palette.grey[50], height: '100%', overflowY: 'auto' }}>
          {renderBody()}
        </Paper>

        {renderFooter()}
      </Drawer>
    </>
  );
});

export default Addon;
